import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Wrench, CheckCircle, Clock, Users } from 'lucide-react';
import PhotoUpload from '@/components/PhotoUpload';

const WarrantyEquipmentPage = () => {
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    underWarranty: 0,
    registeredToday: 0,
    contractorCount: 0
  });

  const [formData, setFormData] = useState({
    serialNumber: '',
    modelNumber: '',
    manufacturerId: '',
    installDate: '',
    installAddress: '',
    contractorId: '',
    ahriReference: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!supabase) return;

    try {
      // Load equipment with related data
      const { data: equipmentData } = await supabase
        .from('equipment')
        .select(`
          *,
          manufacturer:manufacturers(*),
          installer:contractors(*),
          property:properties(*),
          agreements:warranty_agreements(*)
        `)
        .order('created_at', { ascending: false });

      setEquipment(equipmentData || []);

      // Load manufacturers
      const { data: manufacturersData } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('active', true)
        .order('name');

      setManufacturers(manufacturersData || []);

      // Load contractors
      const { data: contractorsData } = await supabase
        .from('contractors')
        .select('*')
        .eq('active', true)
        .order('legal_name');

      setContractors(contractorsData || []);

      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const totalEquipment = equipmentData?.length || 0;
      const registeredToday = equipmentData?.filter(e =>
        e.created_at?.startsWith(today)
      ).length || 0;
      const underWarranty = equipmentData?.filter(e =>
        e.agreements?.some((a: any) => a.status === 'active')
      ).length || 0;

      setStats({
        total: totalEquipment,
        underWarranty,
        registeredToday,
        contractorCount: contractorsData?.length || 0
      });

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    try {
      // First create property if needed
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .insert({
          street: formData.installAddress,
          city: 'Orlando', // Default for now
          state: 'FL',
          zip: '32801' // Default for now
        })
        .select()
        .single();

      if (propertyError) throw propertyError;

      // Then create equipment
      const { data: equipmentData, error: equipmentError } = await supabase
        .from('equipment')
        .insert({
          serial_number: formData.serialNumber,
          model_number: formData.modelNumber,
          manufacturer_id: formData.manufacturerId,
          install_date: formData.installDate,
          installer_contractor_id: formData.contractorId,
          property_id: propertyData.id,
          ahri_reference: formData.ahriReference || null
        })
        .select()
        .single();

      if (equipmentError) throw equipmentError;

      // Reset form and reload data
      setFormData({
        serialNumber: '',
        modelNumber: '',
        manufacturerId: '',
        installDate: '',
        installAddress: '',
        contractorId: '',
        ahriReference: ''
      });
      setShowRegisterForm(false);
      loadData();

    } catch (error) {
      console.error('Error registering equipment:', error);
      alert('Error registering equipment. Please try again.');
    }
  };

  return (
    <AdminPageLayout
      title="🔧 Equipment Registry"
      subtitle="Manage HVAC equipment serial numbers, models, and installation data"
      actions={
        <button
          onClick={() => setShowRegisterForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-white"
        >
          ➕ Register Equipment
        </button>
      }
    >
      {/* Stats */}
      <StatsGrid cols={4}>
        <StatCard label="Total Equipment" value={stats.total.toLocaleString()} icon={Wrench} />
        <StatCard label="Under Warranty" value={stats.underWarranty.toLocaleString()} icon={CheckCircle} variant="success" />
        <StatCard label="Registered Today" value={stats.registeredToday} icon={Clock} />
        <StatCard label="Contractors" value={stats.contractorCount} icon={Users} />
      </StatsGrid>

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search by serial number..."
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Manufacturers</option>
            <option>Trane</option>
            <option>Carrier</option>
            <option>Lennox</option>
            <option>Rheem</option>
            <option>Goodman</option>
          </select>
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Status</option>
            <option>Under Warranty</option>
            <option>Expired</option>
            <option>No Agreement</option>
          </select>
          <input
            type="text"
            placeholder="ZIP Code..."
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </Card>

      {/* Equipment Table */}
      <Card className="p-6">
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="p-4 text-left text-slate-300 font-semibold">Serial Number</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Model</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Manufacturer</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Install Date</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Address</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Contractor</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Warranty Status</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    Loading equipment...
                  </td>
                </tr>
              ) : equipment.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    No equipment registered yet. Click "Register Equipment" to add some.
                  </td>
                </tr>
              ) : (
                equipment.map((item) => (
                  <EquipmentRow
                    key={item.id}
                    item={item}
                    onPhotosUpdate={loadData}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Register Equipment Modal */}
      {showRegisterForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">🔧 Register New Equipment</h2>
              <button
                onClick={() => setShowRegisterForm(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Serial Number *
                  </label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Enter equipment serial number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Model Number *
                  </label>
                  <input
                    type="text"
                    value={formData.modelNumber}
                    onChange={(e) => setFormData({...formData, modelNumber: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Enter model number"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Manufacturer *
                  </label>
                  <select
                    value={formData.manufacturerId}
                    onChange={(e) => setFormData({...formData, manufacturerId: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select manufacturer</option>
                    {manufacturers.map((manufacturer) => (
                      <option key={manufacturer.id} value={manufacturer.id}>
                        {manufacturer.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Install Date *
                  </label>
                  <input
                    type="date"
                    value={formData.installDate}
                    onChange={(e) => setFormData({...formData, installDate: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Installation Address *
                </label>
                <input
                  type="text"
                  value={formData.installAddress}
                  onChange={(e) => setFormData({...formData, installAddress: e.target.value})}
                  className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  placeholder="123 Main St, Orlando, FL 32801"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Installing Contractor *
                </label>
                <select
                  value={formData.contractorId}
                  onChange={(e) => setFormData({...formData, contractorId: e.target.value})}
                  className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Select contractor</option>
                  {contractors.map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.legal_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  AHRI Reference Number (Optional)
                </label>
                <input
                  type="text"
                  value={formData.ahriReference}
                  onChange={(e) => setFormData({...formData, ahriReference: e.target.value})}
                  className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  placeholder="AHRI certificate reference"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Installation Photos
                </label>
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center">
                  <div className="text-slate-400 mb-2">📷</div>
                  <div className="text-slate-400">Drag and drop photos or click to browse</div>
                  <input type="file" multiple accept="image/*" className="hidden" />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRegisterForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Register Equipment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

const EquipmentRow = ({
  item,
  onPhotosUpdate
}: {
  item: any;
  onPhotosUpdate: () => void;
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const warrantyStatus = item.agreements?.some((a: any) => a.status === 'active') ? 'active' : 'none';
  const address = `${item.property?.street || ''}, ${item.property?.city || ''}, ${item.property?.state || ''} ${item.property?.zip || ''}`.trim();

  const statusColors = {
    active: 'bg-green-900 text-green-400',
    pending: 'bg-yellow-900 text-yellow-400',
    expired: 'bg-red-900 text-red-400',
    none: 'bg-slate-700 text-slate-400'
  };

  const statusLabels = {
    active: 'Active',
    pending: 'Pending',
    expired: 'Expired',
    none: 'No Warranty'
  };

  return (
    <>
      <tr className="border-b border-slate-800 hover:bg-slate-800/50">
        <td className="p-4">
          <div className="flex items-center space-x-3">
            <span className="font-mono text-blue-400 font-semibold">{item.serial_number}</span>
            {item.photos && item.photos.length > 0 && (
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-green-400">{item.photos.length}</span>
              </div>
            )}
          </div>
        </td>
        <td className="p-4 text-slate-300">{item.model_number}</td>
        <td className="p-4 text-slate-300">{item.manufacturer?.name || 'Unknown'}</td>
        <td className="p-4 text-slate-300">{item.install_date}</td>
        <td className="p-4 text-slate-300">{address}</td>
        <td className="p-4 text-slate-300">{item.installer?.legal_name || 'Unknown'}</td>
        <td className="p-4">
          <span className={`px-2 py-1 rounded text-sm font-medium ${statusColors[warrantyStatus]}`}>
            {statusLabels[warrantyStatus]}
          </span>
        </td>
        <td className="p-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setShowDetails(true)}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              View Details
            </button>
            <button className="text-green-400 hover:text-green-300 text-sm">Create Agreement</button>
          </div>
        </td>
      </tr>

      {/* Equipment Details Modal */}
      {showDetails && (
        <EquipmentDetailsModal
          equipment={item}
          onClose={() => setShowDetails(false)}
          onPhotosUpdate={onPhotosUpdate}
        />
      )}
    </>
  );
};

const EquipmentDetailsModal = ({
  equipment,
  onClose,
  onPhotosUpdate
}: {
  equipment: any;
  onClose: () => void;
  onPhotosUpdate: () => void;
}) => {
  const handlePhotosUpdate = (photos: string[]) => {
    // Update the local equipment object
    equipment.photos = photos;
    onPhotosUpdate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">🔧 Equipment Details</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Equipment Information */}
          <div className="space-y-4">
            <div className="bg-slate-800 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-200 mb-3">Equipment Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Serial Number:</span>
                  <span className="font-mono text-blue-400">{equipment.serial_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Model:</span>
                  <span className="text-slate-300">{equipment.model_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Manufacturer:</span>
                  <span className="text-slate-300">{equipment.manufacturer?.name || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Install Date:</span>
                  <span className="text-slate-300">{equipment.install_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Status:</span>
                  <span className="text-green-400">{equipment.status}</span>
                </div>
                {equipment.ahri_reference && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">AHRI Reference:</span>
                    <span className="text-slate-300">{equipment.ahri_reference}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Location Information */}
            {equipment.property && (
              <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-200 mb-3">Installation Location</h3>
                <div className="space-y-1 text-sm text-slate-300">
                  <p>{equipment.property.street}</p>
                  <p>{equipment.property.city}, {equipment.property.state} {equipment.property.zip}</p>
                  {equipment.property.county && <p>County: {equipment.property.county}</p>}
                </div>
              </div>
            )}

            {/* Installer Information */}
            {equipment.installer && (
              <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-200 mb-3">Installing Contractor</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Company:</span>
                    <span className="text-slate-300">{equipment.installer.legal_name}</span>
                  </div>
                  {equipment.installer.dba && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">DBA:</span>
                      <span className="text-slate-300">{equipment.installer.dba}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">License:</span>
                    <span className="text-slate-300">{equipment.installer.license_number}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Photo Management */}
          <div>
            <PhotoUpload
              equipmentId={equipment.id}
              existingPhotos={equipment.photos || []}
              onPhotosUpdate={handlePhotosUpdate}
              maxPhotos={8}
            />
          </div>
        </div>

        {/* Warranty Agreements */}
        {equipment.agreements && equipment.agreements.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-slate-200 mb-3">Warranty Agreements</h3>
            <div className="space-y-2">
              {equipment.agreements.map((agreement: any) => (
                <div key={agreement.id} className="bg-slate-800 p-3 rounded-lg text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Agreement: {agreement.agreement_number}</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      agreement.status === 'active' ? 'bg-green-900 text-green-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {agreement.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarrantyEquipmentPage;
